/*
  CODEX CUSTOMIZATION
  File: custom-wishlist.js
  Scope: Wishlist page template
  Purpose:
  - Use Wishlist Power's JavaScript API as the wishlist data source.
  - Render OOL-owned wishlist product cards with product metafield images/text.
*/

(() => {
  const ROOT_SELECTOR = '[data-codex-wishlist-root]';
  const GRID_SELECTOR = '[data-codex-wishlist-grid]';
  const STATUS_SELECTOR = '[data-codex-wishlist-status]';
  const SHARE_SELECTOR = '[data-codex-wishlist-share]';
  const SHARE_CUSTOM_ELEMENT = 'ooo-wl-page-share-button';
  const PRODUCT_CARD_VIEW = 'codex-wishlist-card-data';
  const SHOPIFY_PREVIEW_PARAM_NAMES = ['preview_theme_id', '_ab', '_fd', '_sc'];

  const productDataCache = new Map();
  const pendingProductDataRequests = new Map();
  const initializedRoots = new WeakSet();

  function getShopRootUrl() {
    const root = window.Shopify?.routes?.root || '/';

    return root.endsWith('/') ? root : `${root}/`;
  }

  function getProductDataUrl(handle) {
    const url = new URL(`${getShopRootUrl()}products/${encodeURIComponent(handle)}`, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);

    url.searchParams.set('view', PRODUCT_CARD_VIEW);

    SHOPIFY_PREVIEW_PARAM_NAMES.forEach((paramName) => {
      const paramValue = currentParams.get(paramName);

      if (paramValue) {
        url.searchParams.set(paramName, paramValue);
      }
    });

    return url.toString();
  }

  function waitForWishlistApi(timeout = 20000) {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const tick = () => {
        const api = window.Maestrooo?.wishlist;

        if (api && typeof api.getContent === 'function') {
          resolve(api);
          return;
        }

        if (Date.now() - startTime > timeout) {
          reject(new Error('Wishlist Power API was not available in time.'));
          return;
        }

        window.setTimeout(tick, 100);
      };

      tick();
    });
  }

  function fetchProductCardData(handle) {
    if (productDataCache.has(handle)) {
      return Promise.resolve(productDataCache.get(handle));
    }

    if (pendingProductDataRequests.has(handle)) {
      return pendingProductDataRequests.get(handle);
    }

    const request = fetch(getProductDataUrl(handle), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load wishlist product data for ${handle}.`);
        }

        return response.json();
      })
      .then((data) => {
        productDataCache.set(handle, data);
        pendingProductDataRequests.delete(handle);

        return data;
      })
      .catch((error) => {
        pendingProductDataRequests.delete(handle);
        throw error;
      });

    pendingProductDataRequests.set(handle, request);

    return request;
  }

  async function mapWithConcurrency(items, concurrency, mapper) {
    const results = [];
    let currentIndex = 0;

    async function worker() {
      while (currentIndex < items.length) {
        const itemIndex = currentIndex;
        currentIndex += 1;
        results[itemIndex] = await mapper(items[itemIndex]);
      }
    }

    const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
    await Promise.all(workers);

    return results;
  }

  function escapeHTML(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttribute(value) {
    return escapeHTML(value);
  }

  function getStrings(root) {
    return {
      loading: root.dataset.loadingText || 'Loading your favorites...',
      emptyTitle: root.dataset.emptyTitle || 'Your wishlist is empty',
      emptyText: root.dataset.emptyText || '',
      error: root.dataset.errorText || 'We could not load your wishlist. Please refresh and try again.',
      addToCart: root.dataset.addToCartText || 'Add to cart',
      soldOut: root.dataset.soldOutText || 'Sold out',
      remove: root.dataset.removeText || 'Remove',
      decrease: root.dataset.decreaseText || 'Decrease quantity',
      increase: root.dataset.increaseText || 'Increase quantity',
      quantityFor: root.dataset.quantityText || 'Quantity for'
    };
  }

  function setStatus(state, message, mode = 'text') {
    state.status.hidden = false;
    state.status.classList.toggle('codex-wishlist__status--empty', mode === 'empty');
    state.status.classList.toggle('codex-wishlist__status--error', mode === 'error');

    if (mode === 'empty') {
      state.status.innerHTML = `
        <div class="empty-state">
          <div class="prose">
            <p class="h5">${escapeHTML(state.strings.emptyTitle)}</p>
            ${state.strings.emptyText ? `<p>${escapeHTML(state.strings.emptyText)}</p>` : ''}
          </div>
        </div>
      `;
    } else {
      state.status.textContent = message;
    }
  }

  function hideStatus(state) {
    state.status.hidden = true;
    state.status.textContent = '';
  }

  function setShareVisible(state, visible) {
    if (state.share) {
      state.share.hidden = !visible;
    }
  }

  function isSharedWishlist() {
    const isSharedWishlistFunction = window.ooo?.wishlist?.functions?.isSharedWishlist;

    return typeof isSharedWishlistFunction === 'function' && isSharedWishlistFunction();
  }

  async function prepareNativeShare(state) {
    const wishlist = window.ooo?.wishlist;
    const functions = wishlist?.functions;

    if (
      !state.share
      || !functions
      || !wishlist.customerSharedId
      || functions.isSharedWishlist()
      || !functions.isWishlistAccessible()
      || !functions.isSharedWishlistAccessible()
      || !functions.showShareWishlistButton()
    ) {
      return false;
    }

    if (!window.customElements.get(SHARE_CUSTOM_ELEMENT)) {
      await Promise.race([
        window.customElements.whenDefined(SHARE_CUSTOM_ELEMENT),
        new Promise((resolve) => window.setTimeout(resolve, 5000))
      ]);
    }

    const shareButton = state.share.querySelector(SHARE_CUSTOM_ELEMENT);
    const copyButton = shareButton?.querySelector('ooo-wl-copy-button');

    if (!shareButton || !copyButton || !window.customElements.get(SHARE_CUSTOM_ELEMENT)) {
      return false;
    }

    let signedUrl;

    try {
      signedUrl = new URL(await functions.getSharedWishlistURL(), window.location.origin);
    } catch (error) {
      return false;
    }

    if (!signedUrl.searchParams.get('id') || !signedUrl.searchParams.get('hmac')) {
      return false;
    }

    shareButton.copyButtonUrl = signedUrl.toString();
    shareButton.shareUrl = signedUrl.toString();

    return true;
  }

  function normalizeWishlistItems(result) {
    const products = Array.isArray(result?.products) ? result.products : [];

    return products.filter((item) => item?.handle);
  }

  function getWishlistEntryKey(item) {
    return `${item.handle}:${item.variant_id || 'product'}:${item.id || ''}`;
  }

  function getVariantForWishlistItem(productData, item) {
    const wishlistVariantId = Number(item.variant_id);
    const variants = Array.isArray(productData.variants) ? productData.variants : [];

    return variants.find((variant) => Number(variant.id) === wishlistVariantId)
      || variants.find((variant) => Number(variant.id) === Number(productData.selected_or_first_available_variant_id))
      || variants.find((variant) => variant.available)
      || variants[0]
      || null;
  }

  function normalizeQuantityRule(variant) {
    const rule = variant?.quantity_rule || {};
    const min = Number(rule.min) > 0 ? Number(rule.min) : 1;
    const increment = Number(rule.increment) > 0 ? Number(rule.increment) : 1;
    const max = Number(rule.max) > 0 ? Number(rule.max) : null;

    return { min, increment, max };
  }

  function getImageMarkup(image, className, loading = 'lazy', ariaHidden = false) {
    if (!image?.src) {
      return '';
    }

    const width = image.width ? ` width="${escapeAttribute(image.width)}"` : '';
    const height = image.height ? ` height="${escapeAttribute(image.height)}"` : '';
    const srcset = image.srcset ? ` srcset="${escapeAttribute(image.srcset)}"` : '';
    const hidden = ariaHidden ? ' aria-hidden="true"' : '';
    const alt = ariaHidden ? '' : escapeAttribute(image.alt || '');

    return `<img src="${escapeAttribute(image.src)}"${srcset}${width}${height} sizes="(max-width: 699px) 45vw, (max-width: 999px) 30vw, 25vw" loading="${loading}" class="${className}" alt="${alt}"${hidden}>`;
  }

  function getCardMediaMarkup(productData) {
    if (!productData.main_image) {
      return '';
    }

    const primaryImage = getImageMarkup(
      productData.main_image,
      'product-card__image product-card__image--primary object-fill-safe aspect-square',
      'lazy'
    );
    const secondaryImage = productData.hover_image
      ? getImageMarkup(productData.hover_image, 'product-card__image product-card__image--secondary object-fill', 'lazy', true)
      : '';

    return `
      <div class="product-card__figure">
        <a href="${escapeAttribute(productData.url)}" data-instant tabindex="-1" aria-hidden="true">
          ${primaryImage}
          ${secondaryImage}
        </a>
      </div>
    `;
  }

  function getQuantitySelectorMarkup(entryKey, variant, strings, productTitle) {
    const rule = normalizeQuantityRule(variant);
    const max = rule.max ? ` max="${rule.max}"` : '';

    return `
      <quantity-selector class="quantity-selector codex-wishlist-card__quantity">
        <button type="button" tabindex="-1" class="quantity-selector__button" aria-label="${escapeAttribute(strings.decrease)}">-</button>
        <input id="codex-wishlist-quantity-${escapeAttribute(entryKey)}" type="number" is="quantity-input" inputmode="numeric" class="quantity-selector__input" name="quantity" value="${rule.min}" step="${rule.increment}" min="${rule.min}"${max} autocomplete="off" aria-label="${escapeAttribute(`${strings.quantityFor}: ${productTitle}`)}">
        <button type="button" tabindex="-1" class="quantity-selector__button" aria-label="${escapeAttribute(strings.increase)}">+</button>
      </quantity-selector>
    `;
  }

  function getWishlistCardMarkup(item, productData, state) {
    const variant = getVariantForWishlistItem(productData, item);
    const entryKey = getWishlistEntryKey(item);
    const hasHover = Boolean(productData.hover_image);
    const cardClass = `product-card product-card--blends codex-wishlist-card${hasHover ? ' product-card--show-secondary-media' : ''}`;
    const title = productData.title || item.title || item.handle;
    const variantTitle = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
    const priceHtml = variant?.price_html || '';
    const canAddToCart = Boolean(variant?.available);
    const removeButton = state.isSharedWishlist
      ? ''
      : `
        <button type="button" class="codex-wishlist-card__remove" data-codex-wishlist-remove data-handle="${escapeAttribute(item.handle)}" data-variant-id="${escapeAttribute(item.variant_id || '')}" aria-label="${escapeAttribute(state.strings.remove)} ${escapeAttribute(title)}">
          <span aria-hidden="true">×</span>
        </button>
      `;

    return `
      <product-card handle="${escapeAttribute(productData.handle)}" class="${cardClass}" data-codex-wishlist-entry="${escapeAttribute(entryKey)}">
        ${removeButton}

        ${getCardMediaMarkup(productData)}

        <div class="product-card__info">
          <a href="${escapeAttribute(productData.url)}" class="product-card__info-link" data-instant>
            ${productData.location ? `<span class="product-card__top-text text-xs link-faded codex-wishlist-card__top-text" aria-hidden="true">${escapeHTML(productData.location)}</span>` : ''}

            <div class="v-stack gap-0.5 w-full">
            <span class="product-card__title bold">${escapeHTML(title)}</span>
            ${productData.location ? `<span class="sr-only">, </span><span class="sr-only">From: ${escapeHTML(productData.location)}</span>` : ''}
            ${productData.card_hook ? `<span class="sr-only">, </span><span class="product-card__middle-text text-sm link-faded codex-wishlist-card__middle-text">${escapeHTML(productData.card_hook)}</span>` : ''}
            ${variantTitle ? `<span class="sr-only">, </span><span class="text-sm text-subdued">${escapeHTML(variantTitle)}</span>` : ''}
            <span class="sr-only">, </span>
            ${priceHtml}
            </div>
          </a>

          <form class="codex-wishlist-card__form" data-codex-wishlist-add-to-cart data-variant-id="${escapeAttribute(variant?.id || '')}">
            ${getQuantitySelectorMarkup(entryKey, variant, state.strings, title)}
            <button type="submit" is="custom-button" class="button codex-wishlist-card__add-button" ${canAddToCart ? '' : 'disabled'}>
              ${escapeHTML(canAddToCart ? state.strings.addToCart : (productData.sold_out_label || state.strings.soldOut))}
            </button>
          </form>
        </div>
      </product-card>
    `;
  }

  function renderWishlist(state, wishlistItems, productDataByHandle) {
    const cards = wishlistItems
      .map((item) => {
        const productData = productDataByHandle.get(item.handle);

        return productData ? getWishlistCardMarkup(item, productData, state) : '';
      })
      .filter(Boolean);

    if (!cards.length) {
      state.grid.hidden = true;
      state.grid.innerHTML = '';
      setShareVisible(state, false);
      setStatus(state, '', 'empty');
      return;
    }

    hideStatus(state);
    setShareVisible(state, state.shareReady);
    state.grid.hidden = false;
    state.grid.innerHTML = cards.join('');
  }

  async function loadWishlist(state) {
    setStatus(state, state.strings.loading);
    setShareVisible(state, false);
    state.grid.hidden = true;

    try {
      state.api = state.api || await waitForWishlistApi();
      state.isSharedWishlist = isSharedWishlist();
      const result = await state.api.getContent(!state.isSharedWishlist, state.isSharedWishlist);
      const wishlistItems = normalizeWishlistItems(result);
      state.shareReady = state.isSharedWishlist ? false : await prepareNativeShare(state);

      if (!wishlistItems.length) {
        renderWishlist(state, [], new Map());
        return;
      }

      const handles = Array.from(new Set(wishlistItems.map((item) => item.handle)));
      const productDataList = await mapWithConcurrency(handles, 4, (handle) => fetchProductCardData(handle).catch(() => null));
      const productDataByHandle = new Map();

      handles.forEach((handle, index) => {
        if (productDataList[index]) {
          productDataByHandle.set(handle, productDataList[index]);
        }
      });

      renderWishlist(state, wishlistItems, productDataByHandle);
    } catch (error) {
      state.grid.hidden = true;
      state.grid.innerHTML = '';
      setShareVisible(state, false);
      setStatus(state, state.strings.error, 'error');
    }
  }

  async function addToCart(state, form) {
    const button = form.querySelector('button[type="submit"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const variantId = Number(form.dataset.variantId);
    const quantity = Number(quantityInput?.value) || 1;

    if (!variantId || button?.disabled) {
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${getShopRootUrl()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error('Unable to add wishlist item to cart.');
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: {
          openCartDrawerOnAdd: true
        }
      }));
    } catch (error) {
      setStatus(state, state.strings.error, 'error');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async function removeWishlistItem(state, button) {
    const handle = button.dataset.handle;
    const variantId = Number(button.dataset.variantId) || null;

    if (!handle || !state.api || state.isSharedWishlist) {
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      await state.api.removeProduct(handle, variantId);
      await loadWishlist(state);
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      setStatus(state, state.strings.error, 'error');
    }
  }

  function initializeRoot(root) {
    if (initializedRoots.has(root)) {
      return;
    }

    const grid = root.querySelector(GRID_SELECTOR);
    const status = root.querySelector(STATUS_SELECTOR);
    const share = root.querySelector(SHARE_SELECTOR);

    if (!grid || !status) {
      return;
    }

    initializedRoots.add(root);

    const state = {
      root,
      grid,
      status,
      share,
      shareReady: false,
      isSharedWishlist: false,
      strings: getStrings(root),
      api: null
    };

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const removeButton = target?.closest('[data-codex-wishlist-remove]');

      if (removeButton) {
        event.preventDefault();
        removeWishlistItem(state, removeButton);
      }
    });

    root.addEventListener('submit', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const form = target?.closest('[data-codex-wishlist-add-to-cart]');

      if (!form) {
        return;
      }

      event.preventDefault();
      addToCart(state, form);
    });

    ['ooo-wishlist:product-added', 'ooo-wishlist:product-removed'].forEach((eventName) => {
      document.addEventListener(eventName, () => loadWishlist(state));
    });

    loadWishlist(state);
  }

  function initializeWishlistRoots() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initializeRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWishlistRoots, { once: true });
  } else {
    initializeWishlistRoots();
  }

  document.addEventListener('shopify:section:load', initializeWishlistRoots);
})();
